import * as Tooltip from "@radix-ui/react-tooltip";
import { format, isSameDay } from "date-fns";
import { ChevronLeft, ChevronRight, Clock3, Info, Maximize2, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration, parseUtcDate } from "../../utils";
import { AppSelect } from "../AppSelect";
import { ScreenshotCarousel, ScreenshotCarouselDialog } from "../screenshots";
import { evidenceScreenshots, evidenceSteps, type EvidenceSelection } from "./sessionEvidenceModel";
import { sessionProcesses } from "./sessionStory";
import type { AnalyticsModel } from "./utils";

type Props = {
  model: AnalyticsModel;
  recordingId: string;
  canShowScreenshots: boolean;
  selection: EvidenceSelection;
  onActiveStep: (step: { traceIndex: number; eventIndex: number }) => void;
  onClose: () => void;
};

export default function SessionEvidence({
  model,
  recordingId,
  canShowScreenshots,
  selection,
  onActiveStep,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [scope, setScope] = useState(selection.scope);
  const steps = useMemo(
    () => evidenceSteps(model, { ...selection, scope }),
    [model, selection, scope],
  );
  const shots = useMemo(() => evidenceScreenshots(steps), [steps]);
  const screenshots = useMemo(
    () => shots.map(shot => ({ recordingId, filename: shot.filename, label: shot.event.name })),
    [shots, recordingId],
  );
  const [active, setActive] = useState({
    traceIndex: selection.traceIndex,
    eventIndex: selection.eventIndex,
  });
  const [imageIndex, setImageIndex] = useState(() =>
    Math.max(
      0,
      shots.findIndex(shot =>
        (
          model.processes[selection.traceIndex]?.trace.events[selection.eventIndex]?.screenshots ??
          []
        ).includes(shot.filename),
      ),
    ),
  );
  const [fullscreen, setFullscreen] = useState(false);
  const [countHelpOpen, setCountHelpOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const stepIndex = Math.max(
    0,
    steps.findIndex(
      step => step.traceIndex === active.traceIndex && step.eventIndex === active.eventIndex,
    ),
  );
  const step = steps[stepIndex];
  const currentImage = shots[imageIndex];
  const stepHasImage =
    !!currentImage && (step?.event.screenshots ?? []).includes(currentImage.filename);
  const stepShots = shots.filter(shot => (step?.event.screenshots ?? []).includes(shot.filename));
  const stepShotIndex = stepShots.findIndex(shot => shot.filename === currentImage?.filename) + 1;
  const stepShotDescription = t("sessionStory.stepScreenshotCount", {
    current: stepShotIndex,
    total: stepShots.length,
  });
  const scopeShotDescription = t(`sessionStory.${scope}ScreenshotCount`, {
    current: imageIndex + 1,
    total: shots.length,
  });
  const screenshotCounter = (
    <span className="flex items-center gap-2">
      <span className="flex flex-col items-center gap-0.5">
        <span title={stepShotDescription} className="font-medium text-text">
          <span aria-hidden="true">
            {stepShotIndex} / {stepShots.length}
          </span>
          <span className="sr-only">{stepShotDescription}</span>
        </span>
        <span title={scopeShotDescription}>
          <span aria-hidden="true">
            {imageIndex + 1} / {shots.length}
          </span>
          <span className="sr-only">{scopeShotDescription}</span>
        </span>
      </span>
      <Tooltip.Provider delayDuration={150}>
        <Tooltip.Root open={countHelpOpen} onOpenChange={setCountHelpOpen}>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              aria-label={t("sessionStory.screenshotCounts")}
              data-screenshot-help
              onClick={event => {
                event.preventDefault();
                setCountHelpOpen(open => !open);
              }}
              className="rounded-full p-1 text-text-dim hover:text-text focus-visible:outline-info"
            >
              <Info size={14} aria-hidden="true" />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="top"
              sideOffset={8}
              collisionPadding={16}
              className="pd-theme z-[60] max-w-[calc(100vw-32px)] rounded-lg border border-outline bg-surface p-3 text-xs text-text shadow-lift"
            >
              <p className="font-medium">{stepShotDescription}</p>
              <p className="mt-1 text-text-dim">{scopeShotDescription}</p>
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </span>
  );
  const stepStart = step && parseUtcDate(step.event.start);
  const stepEnd = step && parseUtcDate(step.event.end);
  const runNumber = sessionProcesses(model)
    .find(group => group.name === step?.processName)
    ?.runs.findIndex(run => run.index === step?.traceIndex);
  const changeImage = useCallback(
    (index: number) => {
      setImageIndex(index);
      const shot = shots[index];
      if (shot) setActive({ traceIndex: shot.traceIndex, eventIndex: shot.eventIndex });
    },
    [shots],
  );
  useEffect(() => {
    if (step) onActiveStep({ traceIndex: step.traceIndex, eventIndex: step.eventIndex });
  }, [step, onActiveStep]);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    ref.current?.focus({ preventScroll: true });
  }, []);
  const selectStep = (index: number) => {
    const next = steps[index];
    if (!next) return;
    setActive({ traceIndex: next.traceIndex, eventIndex: next.eventIndex });
    setImageIndex(
      Math.max(
        0,
        shots.findIndex(shot => (next.event.screenshots ?? []).includes(shot.filename)),
      ),
    );
  };
  const changeScope = (next: EvidenceSelection["scope"]) => {
    // Keep the original run/process as the scope anchor, even after browsing the session.
    const nextSteps = evidenceSteps(model, { ...selection, scope: next });
    const retained =
      nextSteps.find(
        item => item.traceIndex === active.traceIndex && item.eventIndex === active.eventIndex,
      ) ?? nextSteps[0];
    const nextShots = evidenceScreenshots(nextSteps);
    setScope(next);
    if (retained) setActive({ traceIndex: retained.traceIndex, eventIndex: retained.eventIndex });
    setImageIndex(
      Math.max(
        0,
        nextShots.findIndex(shot => (retained?.event.screenshots ?? []).includes(shot.filename)),
      ),
    );
  };
  const buttonClass =
    "rounded-md border border-outline p-1.5 text-text-mid hover:bg-muted-surface disabled:opacity-40";
  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="mt-5 border-t border-outline pt-5 outline-none"
      role="region"
      aria-label={t(canShowScreenshots ? "sessionStory.evidence" : "sessionStory.steps")}
      onKeyDown={event => {
        if (
          event.defaultPrevented ||
          event.metaKey ||
          event.ctrlKey ||
          !event.currentTarget.contains(event.target as Node) ||
          (event.target as HTMLElement).closest("[data-screenshot-help]") ||
          (event.target as HTMLElement).tagName === "SELECT"
        )
          return;
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
          event.preventDefault();
          const direction = event.key === "ArrowRight" ? 1 : -1;
          if (canShowScreenshots && stepHasImage && shots.length > 0)
            changeImage((imageIndex + direction + shots.length) % shots.length);
          else selectStep(stepIndex + direction);
        }
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="mr-auto text-sm font-semibold text-text">
          {t(canShowScreenshots ? "sessionStory.evidence" : "sessionStory.steps")}
        </h3>
        <div className="w-40">
          <AppSelect
            label={t("sessionStory.scope")}
            value={scope}
            onChange={changeScope}
            choices={[
              { id: "run", display: t("sessionStory.thisRun") },
              { id: "process", display: t("sessionStory.thisProcess") },
              { id: "session", display: t("sessionStory.entireSession") },
            ]}
          />
        </div>
        <button type="button" className={buttonClass} aria-label={t("close")} onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      {step ? (
        <div
          className={`mt-4 grid gap-4 ${canShowScreenshots ? "@[1000px]:grid-cols-[minmax(200px,1fr)_minmax(0,2fr)]" : ""}`}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={buttonClass}
                aria-label={t("sessionStory.previousStep")}
                disabled={stepIndex === 0}
                onClick={() => selectStep(stepIndex - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs text-text-dim">
                {t("sessionStory.stepCount", { current: stepIndex + 1, total: steps.length })}
              </span>
              <button
                type="button"
                className={buttonClass}
                aria-label={t("sessionStory.nextStep")}
                disabled={stepIndex === steps.length - 1}
                onClick={() => selectStep(stepIndex + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <p className="mt-4 text-xs text-text-dim">
              {step.processName || t("irrelevant_processes")}
              {runNumber !== undefined && runNumber >= 0
                ? ` · ${t("sessionStory.run", { num: runNumber + 1 })}`
                : ""}
            </p>
            <h4 className="mt-1 text-sm font-semibold text-text">{step.event.name}</h4>
            {stepStart && stepEnd && (
              <div className="mt-3 space-y-1.5">
                <p className="flex items-center gap-1.5 text-base font-semibold tabular-nums text-text">
                  <Clock3 size={16} className="text-text-dim" aria-hidden="true" />
                  {formatDuration(Math.max(0, (stepEnd.getTime() - stepStart.getTime()) / 1000))}
                </p>
                <p className="text-xs tabular-nums text-text-dim">
                  {format(stepStart, "MMM d · HH:mm:ss")}–
                  {format(stepEnd, isSameDay(stepStart, stepEnd) ? "HH:mm:ss" : "MMM d · HH:mm:ss")}
                </p>
              </div>
            )}
            {step.event.goal && (
              <p className="mt-3 text-sm leading-6 text-text-mid">{step.event.goal}</p>
            )}
          </div>
          {canShowScreenshots && (
            <div className="relative min-w-0 rounded-lg bg-muted-surface/40 p-2">
              {shots.length > 0 && stepHasImage ? (
                <>
                  <button
                    type="button"
                    className={`${buttonClass} absolute right-3 top-3 z-10 bg-surface/90`}
                    aria-label={t("sessionStory.fullscreen")}
                    onClick={() => setFullscreen(true)}
                  >
                    <Maximize2 size={16} />
                  </button>
                  {!fullscreen && (
                    <ScreenshotCarousel
                      screenshots={screenshots}
                      initialIndex={imageIndex}
                      onIndexChange={changeImage}
                      counterLabel={screenshotCounter}
                      navigationDetails={
                        currentImage && format(currentImage.time, "MMM d · HH:mm:ss")
                      }
                      autoFocus={false}
                      className="shadow-none"
                    />
                  )}
                  <ScreenshotCarouselDialog
                    open={fullscreen}
                    onOpenChange={setFullscreen}
                    screenshots={screenshots}
                    initialIndex={imageIndex}
                    onIndexChange={changeImage}
                    counterLabel={screenshotCounter}
                    navigationDetails={
                      currentImage && format(currentImage.time, "MMM d · HH:mm:ss")
                    }
                  />
                </>
              ) : (
                <div className="flex min-h-40 flex-col items-center justify-center gap-3 p-4 text-center text-sm text-text-dim">
                  <p>{t("sessionStory.noStepScreenshots")}</p>
                  {shots.length > 0 && (
                    <button type="button" className={buttonClass} onClick={() => changeImage(0)}>
                      {t("sessionStory.browseScreenshots")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="mt-4 text-sm text-text-dim">{t("sessionStory.noSteps")}</p>
      )}
    </div>
  );
}
